import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
  // Échec explicite plutôt que des URLs "undefined/api" silencieuses.
  console.error('REACT_APP_BACKEND_URL is not defined - API calls will fail.');
}

const API_URL = `${BACKEND_URL || ''}/api`;

// Instance dédiée : évite de poser le token admin sur les defaults globaux
// d'axios, donc sur tous les appels de l'application.
const client = axios.create({ baseURL: API_URL });

// Un 401 signifie token absent ou expiré : on purge et on renvoie au login.
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      delete client.defaults.headers.common['Authorization'];
      if (!window.location.pathname.startsWith('/admin/login')) {
        window.location.assign('/admin/login');
      }
    }
    return Promise.reject(error);
  }
);

export { client };

// ============= PUBLIC API =============

// Company
export const getCompanyInfo = async () => {
  const response = await client.get(`/company`);
  return response.data;
};

// Services
export const getServices = async () => {
  const response = await client.get(`/services`);
  return response.data;
};

// Benefits
export const getBenefits = async () => {
  const response = await client.get(`/benefits`);
  return response.data;
};

// Testimonials
export const getTestimonials = async () => {
  const response = await client.get(`/testimonials`);
  return response.data;
};

// Projects/Gallery
export const getProjects = async () => {
  const response = await client.get(`/projects`);
  return response.data;
};

// About
export const getAbout = async () => {
  const response = await client.get(`/about`);
  return response.data;
};

// Quote Request
export const submitQuoteRequest = async (data) => {
  const response = await client.post(`/quote-requests`, data);
  return response.data;
};

// Contact Message
export const submitContactMessage = async (data) => {
  const response = await client.post(`/contact-messages`, data);
  return response.data;
};

// ============= ADMIN API =============

// Admin Auth
export const adminLogin = async (email, password) => {
  const response = await client.post(`/admin/login`, { email, password });
  return response.data;
};

// Set auth token for admin requests
export const setAuthToken = (token) => {
  if (token) {
    client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete client.defaults.headers.common['Authorization'];
  }
};

// Admin Services
export const adminCreateService = async (data) => {
  const response = await client.post(`/admin/services`, data);
  return response.data;
};

export const adminUpdateService = async (id, data) => {
  const response = await client.put(`/admin/services/${id}`, data);
  return response.data;
};

export const adminDeleteService = async (id) => {
  await client.delete(`/admin/services/${id}`);
};

// Admin Benefits
export const adminCreateBenefit = async (data) => {
  const response = await client.post(`/admin/benefits`, data);
  return response.data;
};

export const adminUpdateBenefit = async (id, data) => {
  const response = await client.put(`/admin/benefits/${id}`, data);
  return response.data;
};

export const adminDeleteBenefit = async (id) => {
  await client.delete(`/admin/benefits/${id}`);
};

// Admin Testimonials
export const adminGetAllTestimonials = async () => {
  const response = await client.get(`/admin/testimonials`);
  return response.data;
};

export const adminCreateTestimonial = async (data) => {
  const response = await client.post(`/admin/testimonials`, data);
  return response.data;
};

export const adminUpdateTestimonial = async (id, data) => {
  const response = await client.put(`/admin/testimonials/${id}`, data);
  return response.data;
};

export const adminDeleteTestimonial = async (id) => {
  await client.delete(`/admin/testimonials/${id}`);
};

// Admin Projects
export const adminGetAllProjects = async () => {
  const response = await client.get(`/admin/projects`);
  return response.data;
};

export const adminCreateProject = async (data) => {
  const response = await client.post(`/admin/projects`, data);
  return response.data;
};

export const adminUpdateProject = async (id, data) => {
  const response = await client.put(`/admin/projects/${id}`, data);
  return response.data;
};

export const adminDeleteProject = async (id) => {
  await client.delete(`/admin/projects/${id}`);
};

// Admin Company
export const adminUpdateCompany = async (data) => {
  const response = await client.put(`/admin/company`, data);
  return response.data;
};

// Admin About
export const adminUpdateAbout = async (data) => {
  const response = await client.put(`/admin/about`, data);
  return response.data;
};

// Admin Quote Requests
export const adminGetQuoteRequests = async (params = {}) => {
  const response = await client.get('/admin/quote-requests', { params });
  return response.data; // { items, total, skip, limit }
};

export const adminUpdateQuoteRequestStatus = async (id, status) => {
  const response = await client.put(`/admin/quote-requests/${id}`, { status });
  return response.data;
};

// Admin Contact Messages
export const adminGetContactMessages = async (params = {}) => {
  const response = await client.get('/admin/contact-messages', { params });
  return response.data; // { items, total, skip, limit }
};

export const adminUpdateContactMessageStatus = async (id, status) => {
  const response = await client.put(`/admin/contact-messages/${id}`, { status });
  return response.data;
};

// Admin Profile
export const adminChangePassword = async (currentPassword, newPassword) => {
  const response = await client.put('/admin/profile/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return response.data;
};

export const adminChangeEmail = async (newEmail, password) => {
  const response = await client.put('/admin/profile/email', {
    new_email: newEmail,
    password,
  });
  return response.data;
};

// Site Appearance (Public)
export const getAppearance = async () => {
  const response = await client.get(`/appearance`);
  return response.data;
};

// Admin Appearance
export const adminGetAppearance = async () => {
  const response = await client.get(`/admin/appearance`);
  return response.data;
};

export const adminUpdateAppearance = async (data) => {
  const response = await client.put(`/admin/appearance`, data);
  return response.data;
};

// File Upload
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await client.post(`/admin/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteUploadedFile = async (filename) => {
  const response = await client.delete(`/admin/upload/${filename}`);
  return response.data;
};

// ============= PAGES DE SERVICE (SEO) =============
export const getServiceBySlug = async (slug) => {
  const response = await client.get(`/services/slug/${slug}`);
  return response.data;
};

// ============= ZONES DESSERVIES =============
export const getAreas = async () => {
  const response = await client.get('/areas');
  return response.data;
};

export const getArea = async (slug) => {
  const response = await client.get(`/areas/${slug}`);
  return response.data;
};

export const adminGetAreas = async () => {
  const response = await client.get('/admin/areas');
  return response.data;
};

export const adminCreateArea = async (data) => {
  const response = await client.post('/admin/areas', data);
  return response.data;
};

export const adminUpdateArea = async (id, data) => {
  const response = await client.put(`/admin/areas/${id}`, data);
  return response.data;
};

export const adminDeleteArea = async (id) => {
  await client.delete(`/admin/areas/${id}`);
};

// ============= PAGES LEGALES =============
export const getLegalPage = async (slug) => {
  const response = await client.get(`/legal/${slug}`);
  return response.data;
};

export const adminGetLegalPages = async () => {
  const response = await client.get('/admin/legal');
  return response.data;
};

export const adminUpdateLegalPage = async (slug, data) => {
  const response = await client.put(`/admin/legal/${slug}`, data);
  return response.data;
};

// ============= ESTIMATEUR DE PRIX =============
export const getPricing = async () => {
  const response = await client.get('/pricing');
  return response.data;
};

export const estimatePrice = async (data) => {
  const response = await client.post('/estimate', data);
  return response.data;
};

export const adminGetPricing = async () => {
  const response = await client.get('/admin/pricing');
  return response.data;
};

export const adminUpdatePricing = async (data) => {
  const response = await client.put('/admin/pricing', data);
  return response.data;
};

// ============= TABLEAU DE BORD =============
export const adminGetStats = async () => {
  const response = await client.get('/admin/stats');
  return response.data;
};

// ============= REORDONNANCEMENT =============
export const adminReorder = async (ressource, ids) => {
  const response = await client.put(`/admin/reorder/${ressource}`, { ids });
  return response.data;
};

// ============= EXPORT CSV =============
const telechargerCsv = async (chemin, nomFichier) => {
  const response = await client.get(chemin, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const lien = document.createElement('a');
  lien.href = url;
  lien.setAttribute('download', nomFichier);
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  window.URL.revokeObjectURL(url);
};

export const adminExportQuotes = () =>
  telechargerCsv('/admin/quote-requests/export', 'demandes-de-devis.csv');

export const adminExportMessages = () =>
  telechargerCsv('/admin/contact-messages/export', 'messages-de-contact.csv');

// ============= REINITIALISATION DU MOT DE PASSE =============
export const requestPasswordReset = async (email) => {
  const response = await client.post('/admin/password-reset/request', { email });
  return response.data;
};

export const confirmPasswordReset = async (token, newPassword) => {
  const response = await client.post('/admin/password-reset/confirm', {
    token,
    new_password: newPassword,
  });
  return response.data;
};

// ============= AVIS GOOGLE =============
export const getReviews = async () => {
  const response = await client.get('/reviews');
  return response.data; // { settings, items }
};

export const adminGetReviews = async () => {
  const response = await client.get('/admin/reviews');
  return response.data;
};

export const adminSyncReviews = async () => {
  const response = await client.post('/admin/reviews/sync');
  return response.data;
};

export const adminUpdateReviewSettings = async (data) => {
  const response = await client.put('/admin/reviews/settings', data);
  return response.data;
};

export const adminCreateReview = async (data) => {
  const response = await client.post('/admin/reviews', data);
  return response.data;
};

export const adminUpdateReview = async (id, data) => {
  const response = await client.put(`/admin/reviews/${id}`, data);
  return response.data;
};

export const adminDeleteReview = async (id) => {
  await client.delete(`/admin/reviews/${id}`);
};

// ============= COURRIEL =============

// Boîtes de réception (IMAP, multi-boîtes + filtrage par rôle)
export const adminMailboxBoites = async () => {
  const response = await client.get('/admin/mailbox/boites');
  return response.data; // { configure, boites: [{key,label,connecte,non_lus,message?}] }
};

export const adminMailboxMessages = async (params = {}) => {
  const response = await client.get('/admin/mailbox/messages', { params }); // { boite?, limit, offset, search }
  return response.data; // { items, total, offset, limit }
};

export const adminMailboxMessage = async (uid, boite) => {
  const response = await client.get(
    `/admin/mailbox/messages/${encodeURIComponent(uid)}`,
    { params: { boite } },
  );
  return response.data;
};

export const adminMailboxMark = async (uid, boite, lu) => {
  const response = await client.post(
    `/admin/mailbox/messages/${encodeURIComponent(uid)}/seen`,
    null,
    { params: { boite, lu } },
  );
  return response.data;
};

export const adminMailboxArchive = async (uid, boite) => {
  const response = await client.post(
    `/admin/mailbox/messages/${encodeURIComponent(uid)}/archive`, null, { params: { boite } },
  );
  return response.data;
};

export const adminMailboxTrash = async (uid, boite) => {
  const response = await client.post(
    `/admin/mailbox/messages/${encodeURIComponent(uid)}/trash`, null, { params: { boite } },
  );
  return response.data;
};

// Télécharge une pièce jointe (requête authentifiée → blob → téléchargement).
export const adminDownloadAttachment = async (uid, boite, index, filename) => {
  const response = await client.get(
    `/admin/mailbox/messages/${encodeURIComponent(uid)}/attachment/${index}`,
    { params: { boite }, responseType: 'blob' },
  );
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'piece-jointe';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Envoi d'un email libre (via Resend) ; data peut inclure from_mailbox
export const adminSendEmail = async (data) => {
  const response = await client.post('/admin/email/send', data);
  return response.data;
};

// Brouillons
export const adminListDrafts = async () => {
  const response = await client.get('/admin/drafts');
  return response.data; // { items }
};
export const adminCreateDraft = async (data) => {
  const response = await client.post('/admin/drafts', data);
  return response.data;
};
export const adminUpdateDraft = async (id, data) => {
  const response = await client.put(`/admin/drafts/${id}`, data);
  return response.data;
};
export const adminDeleteDraft = async (id) => {
  const response = await client.delete(`/admin/drafts/${id}`);
  return response.data;
};

// Emails envoyés (journal des envois depuis le panel)
export const adminListSent = async () => {
  const response = await client.get('/admin/sent');
  return response.data; // { items }
};

// Mise à jour d'une demande (statut et/ou note interne)
export const adminUpdateQuoteRequest = async (id, data) => {
  const response = await client.put(`/admin/quote-requests/${id}`, data);
  return response.data;
};

export const adminUpdateContactMessage = async (id, data) => {
  const response = await client.put(`/admin/contact-messages/${id}`, data);
  return response.data;
};

// ============= COMPTE & RÔLES =============

// Compte connecté (email, rôle, nom) — sert à connaître les droits.
export const adminGetMe = async () => {
  const response = await client.get('/admin/me');
  return response.data;
};

// Gestion des comptes (réservée à l'owner côté API).
export const adminListUsers = async () => {
  const response = await client.get('/admin/users');
  return response.data;
};

export const adminCreateUser = async (data) => {
  const response = await client.post('/admin/users', data);
  return response.data;
};

export const adminUpdateUser = async (id, data) => {
  const response = await client.put(`/admin/users/${id}`, data);
  return response.data;
};

export const adminDeleteUser = async (id) => {
  await client.delete(`/admin/users/${id}`);
};
